/**
 * toFormValues — coerce a deal/child record into the shape an AntD metadata
 * form expects, for the EDIT path (API record -> form initialValues).
 *
 * Specifically, `Date` / `Timestamp` fields arrive from the API as ISO strings,
 * but AntD's `<DatePicker>` requires a **dayjs** value. The library's
 * `WigMetadataForm` documents that it coerces these ("Date and Timestamp values
 * are coerced to dayjs instances so the <DatePicker> renders correctly"), but
 * that coercion is missing from the shipped runtime bundle — so an unconverted
 * string reaches the picker and antd crashes with
 * `getUDayjs(...).isValid is not a function` when you open a deal for edit.
 *
 * This is the load-direction mirror of `serializeDealForm` (which handles
 * save), and exists for the same reason: the library's form-value helpers are
 * absent at runtime, so we supply local stand-ins.
 *
 * @param {Record<string, unknown>} values  — the API record (e.g. a deal)
 * @param {Array<object>} metadata          — UIMetadata rows for the table
 * @returns {Record<string, unknown>}        — values with date fields as dayjs
 */
import dayjs from "dayjs";

const DATE_TYPES = new Set(["Date", "Timestamp"]);

export const toFormValues = (values, metadata) => {
  if (!values || typeof values !== "object") return values || {};

  const dateFields = new Set(
    (Array.isArray(metadata) ? metadata : [])
      .filter((m) => m && DATE_TYPES.has(m.data_type))
      .map((m) => m.field)
  );
  if (dateFields.size === 0) return values;

  let out = values;
  for (const field of dateFields) {
    const raw = values[field];
    if (raw == null || raw === "") continue;
    // Already a dayjs instance (duck-typed, mirrors serializeDealForm) — leave it.
    if (typeof raw === "object" && typeof raw.isValid === "function") continue;
    const d = dayjs(raw);
    if (!d.isValid()) continue; // leave unparseable values untouched
    if (out === values) out = { ...values };
    out[field] = d;
  }
  return out;
};

export default toFormValues;
